import { Mixins, Component, Prop } from 'vue-property-decorator'
import MixinInterface from '@/_mixins/index'

import DialogTemplate from '@/_components/dialog/template/template.vue'
import UserAvatar from '@/_components/avatar/user/user.vue'
import LogAuthorization from '@/views/Authorization/components/logAuthorization/logAuthorization.vue'
import Permissions from '@/views/Authorization/components/permissions/permissions.vue'

import ProjectUser from '@/views/Authorization/models/projectUser'
import Log from '@/views/Log/models/log'
import { COURSE_COMPLETIONS_QUERY } from '@/views/Authorization/dialogs/userInfo/queries'
import CourseItem from '@/views/Project/PSI/components/courseItem.vue'

type CourseCompletion = {
    id: string
    course: {
        code: string
        description: string
        id: string
        names: Array<{ language: string; value: string }>
    }
}

class CourseCompletionViewModel {
    id: string
    course: {
        code: string
        description: string
        names: Record<string, string>
    }

    constructor(courseCompletion: CourseCompletion) {
        this.id = courseCompletion.id
        this.course = {
            code: courseCompletion.course.code,
            description: courseCompletion.course.description,
            names: courseCompletion.course.names.reduce(
                (langValuePairs, name: Record<string, string>) => {
                    langValuePairs[name.language] = name.value
                    return langValuePairs
                },
                {}
            ),
        }
    }
}

@Component({
    inheritAttrs: false,
    name: 'UserInfo',
    components: { CourseItem, DialogTemplate, UserAvatar, Permissions, LogAuthorization },
})
export default class UserInfo extends Mixins<MixinInterface>() {
    @Prop({ required: true }) props!: any

    get user(): ProjectUser {
        return this.props.user
    }

    get userName(): string {
        return this.user.userName
    }

    get contractors() {
        return this.user.contractorAssignments.map((assignment) => {
            const roles = assignment.roleAssignments.map((ra) => ra.role.name).filter(Boolean)
            return {
                namePath: assignment.contractor.namePath,
                name: assignment.contractor.name,
                roles,
                noOfRoles: roles.length,
            }
        })
    }

    // INFINITELOADING can only be added when view is mounted
    logs: Array<Log> = []
    timeStamp: string | null = new Date().toISOString()
    loading = false
    isFetchingCompletedCourses = false
    courseCompletions: Array<CourseCompletionViewModel> = []

    isMounted = false

    mounted() {
        this.isMounted = true
    }

    async getCompletedCourses() {
        try {
            this.isFetchingCompletedCourses = true
            const result = await this.$apollo.query({
                query: COURSE_COMPLETIONS_QUERY,
                variables: {
                    filter: {
                        user: this.user.userId,
                        project: this.currentProjectId,
                    },
                    first: 50,
                },
                fetchPolicy: 'network-only',
            })
            this.courseCompletions = result.data?.courseCompletions?.nodes?.map(
                (item) => new CourseCompletionViewModel(item)
            )
        } finally {
            this.isFetchingCompletedCourses = false
        }
    }

    async getLogs($state: any) {
        if (this.currentProjectId) {
            let res: Array<Log> = await this.user
                .getProjectUserLog(this.currentProjectId, this.timeStamp)
                .catch(() => {
                    $state.complete()
                })

            this.logs = [...this.logs, ...res]

            if (res.length == Log.limit) {
                let lastElement = res?.pop()
                this.timeStamp = lastElement ? lastElement?.['time'] : null
                $state.loaded()
            } else {
                this.timeStamp = null
                if (this.logs.length > 0) {
                    $state.loaded()
                }
                $state.complete()
            }
        }
    }
}
